import React, {useEffect, useMemo, useRef, useState} from 'react';
import CIcon from '@coreui/icons-react';
import '../../../scss/styles-perfis.scss';

// Permissoes de um perfil (#484909), pensadas pelo que o perfil pode FAZER e
// nao pelo mapa tecnico: cada programa tem um nivel (sem acesso, consultar,
// completo ou personalizado) e o personalizado abre as acoes com nome legivel.
//
// Concessoes gravadas no perfil:
//   modulo:<id>    modulo inteiro, inclusive programas futuros
//   programa:<id>  programa inteiro (Completo), inclusive acoes futuras
//   consulta:<id>  rotas de leitura do programa (Consultar)
//   rota:<programa>:<chave>  uma acao marcada no programa (Personalizado);
//                            cada programa tem as suas, mesmo em rota compartilhada
//   rota:<chave>   rota fora do mapa, sem programa

export interface Acao {
    chave: string,
    metodo: string,
    caminho: string,
    rotulo: string,
    recurso: string,
    leitura: boolean,
    controller: string
}

export interface ProgramaPerm {
    id: string,
    label: string,
    icone: string | null,
    grupo: string | null,
    restricao: string | null,
    rotas: Acao[]
}

export interface ModuloPerm {
    id: string,
    label: string,
    descricao: string | null,
    icone: string | null,
    programas: ProgramaPerm[]
}

export interface ArvorePerm {
    modulos: ModuloPerm[],
    naoClassificadas: Acao[]
}

type Nivel = 'nenhum' | 'consulta' | 'completo' | 'personalizado';

const NIVEIS: {id: Nivel, rotulo: string, ajuda: string}[] = [
    {id: 'nenhum', rotulo: 'Sem acesso', ajuda: 'O programa não aparece para quem tem este perfil.'},
    {id: 'consulta', rotulo: 'Consultar', ajuda: 'Vê e pesquisa, sem alterar nada. Consultas criadas no futuro entram sozinhas.'},
    {id: 'completo', rotulo: 'Completo', ajuda: 'Tudo do programa, inclusive ações criadas no futuro.'},
    {id: 'personalizado', rotulo: 'Personalizado', ajuda: 'Você escolhe ação por ação.'},
];

const RESTRICOES: {[id: string]: {rotulo: string, ajuda: string}} = {
    gerentes: {rotulo: 'Só gerentes', ajuda: 'Além do perfil, só funciona para quem está na lista de gerentes.'},
    infra: {rotulo: 'Só equipe de Infra', ajuda: 'Além do perfil, só funciona para a lista da Central de Infra, mesmo para master.'},
};

const FORA_DO_MAPA = '_fora';

/** Concessao de uma acao marcada num programa. */
const chaveAcao = (programa: ProgramaPerm, acao: Acao) => `rota:${programa.id}:${acao.chave}`;

/** Marcada no programa ou, no formato antigo, solta. */
const temAcao = (concessoes: Set<string>, programa: ProgramaPerm, acao: Acao) =>
    concessoes.has(chaveAcao(programa, acao)) || concessoes.has(`rota:${acao.chave}`);

/** Ordem natural das acoes dentro de um recurso: consultar antes de alterar. */
const ORDEM_VERBOS = ['Listar', 'Consultar', 'Buscar', 'Ver', 'Carregar', 'Baixar', 'Verificar', 'Incluir', 'Criar', 'Alterar', 'Salvar', 'Duplicar', 'Ativar', 'Inativar', 'Executar', 'Finalizar', 'Cancelar', 'Excluir', 'Apagar'];
const pesoVerbo = (rotulo: string) => {
    const indice = ORDEM_VERBOS.findIndex(verbo => rotulo.startsWith(verbo));
    return indice === -1 ? ORDEM_VERBOS.length : indice;
};

/** Agrupa as acoes por recurso ("atividade", "agendamento"...), na ordem natural. */
const porRecurso = (acoes: Acao[]): [string, Acao[]][] => {
    const grupos = new Map<string, Acao[]>();
    acoes.forEach(acao => {
        const lista = grupos.get(acao.recurso) ?? [];
        lista.push(acao);
        grupos.set(acao.recurso, lista);
    });
    return Array.from(grupos.entries())
        .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
        .map(([recurso, lista]) => [recurso, lista.sort((a, b) => pesoVerbo(a.rotulo) - pesoVerbo(b.rotulo) || a.rotulo.localeCompare(b.rotulo))]);
};

const CHAVE_CAMINHOS = 'perfil-mostrar-caminhos';

/** Tira acento e caixa para a busca. */
const normalizar = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// ------------------------------------------------------------------ regras

/** Acoes do programa que o perfil libera hoje. */
const liberadas = (concessoes: Set<string>, modulo: ModuloPerm, programa: ProgramaPerm): Set<string> => {
    const inteiro = concessoes.has(`modulo:${modulo.id}`) || concessoes.has(`programa:${programa.id}`);
    const consulta = concessoes.has(`consulta:${programa.id}`);
    return new Set(programa.rotas
        .filter(acao => inteiro || (consulta && acao.leitura) || temAcao(concessoes, programa, acao))
        .map(acao => acao.chave));
};

const nivelDo = (concessoes: Set<string>, modulo: ModuloPerm, programa: ProgramaPerm): Nivel => {
    if (concessoes.has(`modulo:${modulo.id}`) || concessoes.has(`programa:${programa.id}`)) {
        return 'completo';
    }
    const avulsas = programa.rotas.filter(acao => temAcao(concessoes, programa, acao));
    if (concessoes.has(`consulta:${programa.id}`)) {
        return avulsas.some(acao => !acao.leitura) ? 'personalizado' : 'consulta';
    }
    return avulsas.length ? 'personalizado' : 'nenhum';
};

/**
 * Grava no perfil o conjunto de acoes liberadas de um programa, do jeito mais
 * enxuto: tudo vira "programa", todas as leituras viram "consulta" e o resto
 * vai como acao avulsa.
 */
const gravarPrograma = (concessoes: Set<string>, programa: ProgramaPerm, acoes: Set<string>): Set<string> => {
    const nova = new Set(concessoes);
    nova.delete(`programa:${programa.id}`);
    nova.delete(`consulta:${programa.id}`);
    programa.rotas.forEach(acao => {
        nova.delete(chaveAcao(programa, acao));
        nova.delete(`rota:${acao.chave}`);
    });
    if (!acoes.size) {
        return nova;
    }
    if (programa.rotas.every(acao => acoes.has(acao.chave))) {
        nova.add(`programa:${programa.id}`);
        return nova;
    }
    const leituras = programa.rotas.filter(acao => acao.leitura);
    const todasLeituras = leituras.length > 0 && leituras.every(acao => acoes.has(acao.chave));
    if (todasLeituras) {
        nova.add(`consulta:${programa.id}`);
    }
    programa.rotas
        .filter(acao => acoes.has(acao.chave) && !(todasLeituras && acao.leitura))
        .forEach(acao => nova.add(chaveAcao(programa, acao)));
    return nova;
};

const aplicarNivel = (concessoes: Set<string>, programa: ProgramaPerm, nivel: Nivel, atual: Set<string>): Set<string> => {
    switch (nivel) {
        case 'nenhum':
            return gravarPrograma(concessoes, programa, new Set());
        case 'completo':
            return gravarPrograma(concessoes, programa, new Set(programa.rotas.map(acao => acao.chave)));
        case 'consulta':
            return gravarPrograma(concessoes, programa, new Set(programa.rotas.filter(acao => acao.leitura).map(acao => acao.chave)));
        case 'personalizado': {
            // Parte do que ja tinha; do zero, comeca pelas consultas.
            const base = atual.size ? atual : new Set(programa.rotas.filter(acao => acao.leitura).map(acao => acao.chave));
            const nova = gravarPrograma(concessoes, programa, base);
            return nova;
        }
    }
};

interface ResumoModulo {
    texto: string,
    tom: 'completo' | 'parcial' | 'nenhum',
    programas: number
}

const resumoDo = (concessoes: Set<string>, modulo: ModuloPerm): ResumoModulo => {
    if (concessoes.has(`modulo:${modulo.id}`)) {
        return {texto: 'Módulo inteiro', tom: 'completo', programas: modulo.programas.length};
    }
    const comAcesso = modulo.programas.filter(programa => nivelDo(concessoes, modulo, programa) !== 'nenhum').length;
    if (!comAcesso) {
        return {texto: 'Sem acesso', tom: 'nenhum', programas: 0};
    }
    const todosCompletos = modulo.programas.every(programa => nivelDo(concessoes, modulo, programa) === 'completo');
    return {
        texto: todosCompletos ? 'Todos os programas' : `${comAcesso} de ${modulo.programas.length} programas`,
        tom: todosCompletos ? 'completo' : 'parcial',
        programas: comAcesso,
    };
};

// -------------------------------------------------------------- componentes

const Caixa = (props: {marcada: boolean, parcial?: boolean, desabilitada?: boolean, onChange: () => void, rotulo: string}) => (
    <input
        type="checkbox"
        className="perm-caixa"
        checked={props.marcada}
        ref={elemento => { if (elemento) elemento.indeterminate = !!props.parcial && !props.marcada; }}
        disabled={props.desabilitada}
        onChange={props.onChange}
        aria-label={props.rotulo}
    />
);

const ListaAcoes = (props: {
    titulo: string,
    ajuda: string,
    acoes: Acao[],
    marcadas: Set<string>,
    desabilitada: boolean,
    mostrarCaminhos: boolean,
    onAlternar: (chaves: string[], marcar: boolean) => void
}) => {
    if (!props.acoes.length) {
        return null;
    }
    const total = props.acoes.filter(acao => props.marcadas.has(acao.chave)).length;
    const todas = total === props.acoes.length;
    return (
        <div className="perm-acoes-grupo">
            <label className="perm-acoes-titulo">
                <Caixa marcada={todas} parcial={total > 0} desabilitada={props.desabilitada}
                       rotulo={`Todas: ${props.titulo}`}
                       onChange={() => props.onAlternar(props.acoes.map(acao => acao.chave), !todas)}/>
                <span>{props.titulo}</span>
                <span className="perm-contador">{total}/{props.acoes.length}</span>
            </label>
            <p className="perm-acoes-ajuda">{props.ajuda}</p>
            {porRecurso(props.acoes).map(([recurso, acoes]) => {
                // Dois nomes iguais no mesmo recurso: o caminho diferencia.
                const repetidos = new Set(acoes.map(acao => acao.rotulo).filter((rotulo, i, todos) => todos.indexOf(rotulo) !== i));
                return (
                <div key={recurso} className="perm-recurso">
                    <div className="perm-recurso-nome">{recurso || 'geral'}</div>
                    <ul>
                        {acoes.map(acao => (
                            <li key={acao.chave}>
                                <label className="perm-acao" title={`${acao.chave}\n${acao.controller}`}>
                                    <Caixa marcada={props.marcadas.has(acao.chave)} desabilitada={props.desabilitada}
                                           rotulo={`${acao.rotulo} (${recurso})`}
                                           onChange={() => props.onAlternar([acao.chave], !props.marcadas.has(acao.chave))}/>
                                    <span className="perm-acao-nome">{acao.rotulo}</span>
                                    {!props.mostrarCaminhos && repetidos.has(acao.rotulo) && (
                                        <code className="perm-acao-dica">{acao.caminho}</code>
                                    )}
                                    {props.mostrarCaminhos && (
                                        <code className="perm-acao-caminho">
                                            <span className={'perm-metodo perm-metodo-' + acao.metodo.toLowerCase()}>{acao.metodo}</span>
                                            {acao.caminho}
                                        </code>
                                    )}
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
                );
            })}
        </div>
    );
};

interface PermissoesProps {
    arvore: ArvorePerm,
    concessoes: string[],
    onChange: (concessoes: string[]) => void,
    somenteLeitura: boolean
}

const Permissoes = ({arvore, concessoes: lista, onChange, somenteLeitura}: PermissoesProps) => {
    const concessoes = useMemo(() => new Set(lista || []), [lista]);
    const [moduloId, setModuloId] = useState<string>('');
    const [abertos, setAbertos] = useState<{[programa: string]: boolean}>({});
    // Programas que o usuario pos em "Personalizado": continuam assim na tela
    // mesmo quando o que esta marcado coincide com "Consultar" ou "Sem acesso".
    const [personalizando, setPersonalizando] = useState<{[programa: string]: boolean}>({});
    const [busca, setBusca] = useState('');
    // Caminho tecnico (GET /task/list) so para quem pede; lembrado no navegador.
    const [mostrarCaminhos, setMostrarCaminhos] = useState<boolean>(() => {
        try {
            return localStorage.getItem(CHAVE_CAMINHOS) === '1';
        } catch (e) {
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(CHAVE_CAMINHOS, mostrarCaminhos ? '1' : '0');
        } catch (e) {
            // Sem storage, so nao lembra na proxima vez.
        }
    }, [mostrarCaminhos]);

    const modulo = arvore.modulos.find(item => item.id === moduloId) ?? arvore.modulos[0];

    // A lista rola por dentro: ao trocar de modulo ou de busca, volta ao topo.
    const painel = useRef<HTMLElement>(null);
    useEffect(() => {
        painel.current?.scrollTo({top: 0});
    }, [moduloId, busca]);
    const termo = normalizar(busca.trim());

    const salvar = (nova: Set<string>) => onChange(Array.from(nova));

    const mudarNivel = (mod: ModuloPerm, programa: ProgramaPerm, nivel: Nivel) => {
        salvar(aplicarNivel(concessoes, programa, nivel, liberadas(concessoes, mod, programa)));
        setPersonalizando({...personalizando, [programa.id]: nivel === 'personalizado'});
        if (nivel === 'personalizado') {
            setAbertos({...abertos, [programa.id]: true});
        }
    };

    const nivelNaTela = (mod: ModuloPerm, programa: ProgramaPerm): Nivel => {
        const nivel = nivelDo(concessoes, mod, programa);
        return personalizando[programa.id] && nivel !== 'completo' ? 'personalizado' : nivel;
    };

    const alternarAcoes = (mod: ModuloPerm, programa: ProgramaPerm, chaves: string[], marcar: boolean) => {
        const acoes = liberadas(concessoes, mod, programa);
        chaves.forEach(chave => marcar ? acoes.add(chave) : acoes.delete(chave));
        salvar(gravarPrograma(concessoes, programa, acoes));
    };

    const alternarModuloInteiro = (mod: ModuloPerm) => {
        const nova = new Set(concessoes);
        if (nova.has(`modulo:${mod.id}`)) {
            nova.delete(`modulo:${mod.id}`);
        } else {
            // O modulo inteiro cobre tudo: limpa o que ficaria redundante.
            nova.add(`modulo:${mod.id}`);
            mod.programas.forEach(programa => {
                nova.delete(`programa:${programa.id}`);
                nova.delete(`consulta:${programa.id}`);
                programa.rotas.forEach(acao => {
                    nova.delete(chaveAcao(programa, acao));
                    nova.delete(`rota:${acao.chave}`);
                });
            });
        }
        salvar(nova);
    };

    const nivelEmTodos = (mod: ModuloPerm, nivel: Nivel) => {
        let nova = new Set(concessoes);
        nova.delete(`modulo:${mod.id}`);
        mod.programas.forEach(programa => {
            nova = aplicarNivel(nova, programa, nivel, new Set());
        });
        salvar(nova);
    };

    const alternarForaDoMapa = (chaves: string[], marcar: boolean) => {
        const nova = new Set(concessoes);
        chaves.forEach(chave => marcar ? nova.add(`rota:${chave}`) : nova.delete(`rota:${chave}`));
        salvar(nova);
    };

    // Busca: programas (de todos os modulos) que casam pelo nome ou por alguma acao.
    const resultados = useMemo(() => {
        if (!termo) {
            return [];
        }
        const casa = (texto: string | null) => !!texto && normalizar(texto).includes(termo);
        return arvore.modulos.map(mod => ({
            modulo: mod,
            programas: mod.programas
                .map(programa => {
                    const programaCasa = casa(programa.label) || casa(programa.grupo);
                    const acoes = programa.rotas.filter(acao => casa(acao.rotulo) || casa(acao.recurso) || casa(acao.caminho));
                    return {programa, acoes: programaCasa ? null : acoes, casa: programaCasa || acoes.length > 0};
                })
                .filter(item => item.casa),
        })).filter(grupo => grupo.programas.length);
    }, [termo, arvore]);

    const resumos = arvore.modulos.map(mod => ({modulo: mod, resumo: resumoDo(concessoes, mod)}));
    const totalProgramas = resumos.reduce((soma, item) => soma + item.resumo.programas, 0);
    const totalModulos = resumos.filter(item => item.resumo.tom !== 'nenhum').length;
    const foraMarcadas = arvore.naoClassificadas.filter(acao => concessoes.has(`rota:${acao.chave}`)).length;

    const linhaPrograma = (mod: ModuloPerm, programa: ProgramaPerm, acoesFiltradas: Acao[] | null) => {
        const moduloInteiro = concessoes.has(`modulo:${mod.id}`);
        const nivel = nivelNaTela(mod, programa);
        const acoes = liberadas(concessoes, mod, programa);
        const aberto = !!abertos[programa.id] || acoesFiltradas !== null;
        const lista = acoesFiltradas ?? programa.rotas;
        const restricao = programa.restricao ? RESTRICOES[programa.restricao] : null;

        return (
            <div key={programa.id} className={'perm-programa perm-nivel-' + nivel + (aberto ? ' aberto' : '')}>
                <div className="perm-programa-linha">
                    <span className="perm-programa-icone">{programa.icone && <CIcon name={programa.icone}/>}</span>
                    <div className="perm-programa-textos">
                        <div className="perm-programa-nome">
                            {programa.label}
                            {restricao && <span className="perm-selo" title={restricao.ajuda}>{restricao.rotulo}</span>}
                        </div>
                        <div className="perm-programa-detalhe">
                            {programa.grupo && <span>{programa.grupo} · </span>}
                            {acoes.size} de {programa.rotas.length} ações
                            <button type="button" className="perm-link" onClick={() => setAbertos({...abertos, [programa.id]: !abertos[programa.id]})}>
                                {aberto ? 'ocultar ações' : 'ver ações'}
                            </button>
                        </div>
                    </div>
                    <div className="perm-niveis" role="radiogroup" aria-label={`Nível de acesso: ${programa.label}`}>
                        {NIVEIS.map(opcao => (
                            <button
                                key={opcao.id}
                                type="button"
                                role="radio"
                                aria-checked={nivel === opcao.id}
                                className={'perm-nivel perm-nivel-opcao-' + opcao.id + (nivel === opcao.id ? ' ativo' : '')}
                                disabled={somenteLeitura || moduloInteiro}
                                title={moduloInteiro ? 'Liberado pelo módulo inteiro' : opcao.ajuda}
                                onClick={() => mudarNivel(mod, programa, opcao.id)}
                            >
                                {opcao.rotulo}
                            </button>
                        ))}
                    </div>
                </div>
                {aberto && (
                    <div className="perm-acoes">
                        {moduloInteiro && <p className="perm-aviso-pequeno">Liberado pelo módulo inteiro. Desligue-o para escolher ação por ação.</p>}
                        <div className="perm-acoes-colunas">
                            <ListaAcoes titulo="Consultas" ajuda="Só leem dados."
                                        acoes={lista.filter(acao => acao.leitura)} marcadas={acoes}
                                        desabilitada={somenteLeitura || moduloInteiro} mostrarCaminhos={mostrarCaminhos}
                                        onAlternar={(chaves, marcar) => alternarAcoes(mod, programa, chaves, marcar)}/>
                            <ListaAcoes titulo="Alterações" ajuda="Incluem, alteram, excluem ou disparam processos."
                                        acoes={lista.filter(acao => !acao.leitura)} marcadas={acoes}
                                        desabilitada={somenteLeitura || moduloInteiro} mostrarCaminhos={mostrarCaminhos}
                                        onAlternar={(chaves, marcar) => alternarAcoes(mod, programa, chaves, marcar)}/>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const painelModulo = (mod: ModuloPerm) => {
        const inteiro = concessoes.has(`modulo:${mod.id}`);
        return (
            <>
                <header className="perm-painel-cabecalho">
                    <span className="perm-painel-icone">{mod.icone && <CIcon name={mod.icone}/>}</span>
                    <div className="perm-painel-titulo">
                        <h5>{mod.label}</h5>
                        {mod.descricao && <p>{mod.descricao}</p>}
                    </div>
                    <label className={'perm-interruptor' + (somenteLeitura ? ' desabilitado' : '')} title="Inclui os programas e ações que forem criados neste módulo">
                        <input type="checkbox" checked={inteiro} disabled={somenteLeitura} onChange={() => alternarModuloInteiro(mod)}/>
                        <span className="perm-interruptor-trilho"><span/></span>
                        <span>Módulo inteiro</span>
                    </label>
                </header>
                {inteiro ? (
                    <div className="perm-aviso">
                        <CIcon name="cil-check-circle"/>
                        <span>Todos os programas deste módulo estão liberados por completo, inclusive os que forem criados.</span>
                    </div>
                ) : !somenteLeitura && (
                    <div className="perm-atalhos">
                        <span>Em todos os programas:</span>
                        <button type="button" className="perm-link" onClick={() => nivelEmTodos(mod, 'consulta')}>Consultar</button>
                        <button type="button" className="perm-link" onClick={() => nivelEmTodos(mod, 'completo')}>Completo</button>
                        <button type="button" className="perm-link perm-link-suave" onClick={() => nivelEmTodos(mod, 'nenhum')}>Sem acesso</button>
                    </div>
                )}
                <div className="perm-programas">
                    {mod.programas.map(programa => linhaPrograma(mod, programa, null))}
                </div>
            </>
        );
    };

    const painelForaDoMapa = () => (
        <>
            <header className="perm-painel-cabecalho">
                <span className="perm-painel-icone"><CIcon name="cil-warning"/></span>
                <div className="perm-painel-titulo">
                    <h5>Fora do mapa</h5>
                    <p>Rotas novas que ainda não estão em nenhum programa do config/acesso.yaml. Sem perfil, só o master acessa.</p>
                </div>
            </header>
            <div className="perm-acoes perm-acoes-solto">
                <ListaAcoes titulo="Rotas" ajuda="Libere só se souber para que servem."
                            acoes={arvore.naoClassificadas}
                            marcadas={new Set(arvore.naoClassificadas.filter(acao => concessoes.has(`rota:${acao.chave}`)).map(acao => acao.chave))}
                            desabilitada={somenteLeitura} mostrarCaminhos={true} onAlternar={alternarForaDoMapa}/>
            </div>
        </>
    );

    return (
        <div className="perm">
            <div className="perm-topo">
                <div className="perm-resumo">
                    {totalProgramas
                        ? <>Este perfil libera <strong>{totalProgramas} {totalProgramas === 1 ? 'programa' : 'programas'}</strong> em <strong>{totalModulos} {totalModulos === 1 ? 'módulo' : 'módulos'}</strong>.</>
                        : <>Este perfil ainda não libera nada.</>}
                </div>
                <div className="perm-topo-direita">
                <button type="button" className={'perm-alternar' + (mostrarCaminhos ? ' ativo' : '')}
                        aria-pressed={mostrarCaminhos} onClick={() => setMostrarCaminhos(!mostrarCaminhos)}
                        title="Mostra o método e o caminho da API de cada ação">
                    <CIcon name="cil-code"/> Caminhos técnicos
                </button>
                <div className="perm-busca">
                    <CIcon name="cil-search"/>
                    <input type="search" value={busca} placeholder="Buscar programa ou ação…" aria-label="Buscar programa ou ação"
                           onChange={evento => setBusca(evento.target.value)}/>
                    {busca && <button type="button" aria-label="Limpar busca" onClick={() => setBusca('')}>×</button>}
                </div>
                </div>
            </div>

            <div className="perm-corpo">
                <nav className="perm-modulos" aria-label="Módulos">
                    {resumos.map(({modulo: mod, resumo}) => (
                        <button key={mod.id} type="button"
                                className={'perm-modulo' + (!termo && mod.id === modulo?.id ? ' ativo' : '')}
                                onClick={() => { setModuloId(mod.id); setBusca(''); }}>
                            <span className="perm-modulo-icone">{mod.icone && <CIcon name={mod.icone}/>}</span>
                            <span className="perm-modulo-textos">
                                <span className="perm-modulo-nome">{mod.label}</span>
                                <span className={'perm-modulo-resumo perm-tom-' + resumo.tom}>
                                    <i/>{resumo.texto}
                                </span>
                            </span>
                        </button>
                    ))}
                    {arvore.naoClassificadas.length > 0 && (
                        <button type="button"
                                className={'perm-modulo perm-modulo-fora' + (!termo && moduloId === FORA_DO_MAPA ? ' ativo' : '')}
                                onClick={() => { setModuloId(FORA_DO_MAPA); setBusca(''); }}>
                            <span className="perm-modulo-icone"><CIcon name="cil-warning"/></span>
                            <span className="perm-modulo-textos">
                                <span className="perm-modulo-nome">Fora do mapa</span>
                                <span className={'perm-modulo-resumo perm-tom-' + (foraMarcadas ? 'parcial' : 'nenhum')}>
                                    <i/>{arvore.naoClassificadas.length} rotas
                                </span>
                            </span>
                        </button>
                    )}
                </nav>

                <section className="perm-painel" ref={painel}>
                    {termo ? (
                        resultados.length ? resultados.map(grupo => (
                            <div key={grupo.modulo.id} className="perm-resultado">
                                <div className="perm-resultado-modulo">{grupo.modulo.label}</div>
                                <div className="perm-programas">
                                    {grupo.programas.map(item => linhaPrograma(grupo.modulo, item.programa, item.acoes))}
                                </div>
                            </div>
                        )) : (
                            <div className="perm-vazio">Nada encontrado para “{busca}”.</div>
                        )
                    ) : moduloId === FORA_DO_MAPA ? painelForaDoMapa() : modulo && painelModulo(modulo)}
                </section>
            </div>
        </div>
    );
};

export default Permissoes;
